import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  Globe,
  Heart,
  MessageSquare,
  Sparkles,
  Target,
  UserCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { CountrySelect } from "~/components/country-select";
import { LanguageSelect } from "~/components/language-select";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { CustomMultiSelect } from "~/components/ui/custom-multi-select";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Textarea } from "~/components/ui/textarea";
import { authMiddleware } from "~/lib/auth-middleware";
import { cn } from "~/lib/cn";
import {
  CoachDiscoverySchema,
  coachAgesField,
  coachGenderField,
  coachLocationCountryField,
  coachLocationRegionField,
  coachPersonalityField,
  coachingApproachesField,
  coachingFormatsFields,
  coachingPackagesField,
  faithIntegrationsField,
  goalsField,
  otherFaithIntegrationField,
  otherGoalField,
  personalContextField,
  prefersCoachLocationField,
  sessionDeliveryMethodsFeild,
  startTimeField,
  supportField,
  userPersonalityField,
} from "~/lib/schemas/coach-discovery";
import type { CoachDiscoveryForm } from "~/lib/schemas/coach-discovery";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";

import { SettingsCardTitle } from "./-components/settings-card-title";

export const getFormData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { getUserPreferences } = await import("~/lib/server/user");
    const userPreferences = await getUserPreferences(context.user.id);
    return transformUserPreferencesToDiscoveryForm(userPreferences);
  });

export const updateFormData = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ data: CoachDiscoverySchema }))
  .handler(async ({ data, context }) => {
    const values = data.data;
    const embeddingPrompt = `
    This user is seeking a coach with the following preferences:

    Primary Coaching Needs:
    - Main goals: ${values.goals.join(", ")}
    - Other goals: ${values.otherGoal || "Not specified"}

    Coaching Format & Delivery:
    - Coaching Format: ${values.coachingFormats}
    - Session Format: ${values.sessionDeliveryMethods}

    Experience Level & Coaching Packages:
    - Coach Experience Levels: ${values.coachingPackages.join(", ")}

    Coaching Style & Approach
    - Coaching Approaches: ${values.coachingApproaches.join(", ")}

    Coach characteristics Preferences:
    - Preferred coach gender: ${values.coachGender || "No preference"}
    - Faith/religion alignment: ${values.faithIntegration.join(", ")}
    - Language requirements: ${values.preferredLanguages?.join(", ")}
    - Coach Ages: ${values.coachAges.join(", ")}

    Location & Logistics:
    - Prefers coach location: ${values.prefersCoachLocation}
    - Coach location region: ${values.coachLocationRegion || "Not specified"}
    - Coach location country: ${values.coachLocationCountry || "Not specified"}

    Additional Context:
    - Other faith preference: ${values.otherFaithIntegration || "Not specified"}
    `;

    try {
      const userId = context.user.id;

      const [{ getEmbeddingsClient }, { updateUserPreferences }] =
        await Promise.all([
          import("~/lib/server/embeddings-client"),
          import("~/lib/server/user"),
        ]);

      const embedding =
        await getEmbeddingsClient().createEmbedding(embeddingPrompt);

      if (userId) {
        await updateUserPreferences(userId, {
          ...values,
          preferencesEmbedding: embedding,
        });
      }
      return {
        status: "success",
        message: "Preferences updated",
      };
    } catch (error) {
      // Report here, on the server, while the whole error is still in hand.
      //
      // Every failure mode in the block above — an embeddings upstream error, a
      // Hyperdrive/Neon drop on the write, a validation rejection — used to be
      // discarded and replaced with a bare message, so the event Sentry got had
      // no stack below this line. Sentry DCM-T4 recorded eight failed saves for
      // one user in a single minute and the cause was unrecoverable.
      // `reportToSentry` reads postgres fields off the error, so a dropped
      // connection lands in the shared postgres fingerprint bucket.
      //
      // The original must not ride out on the thrown error — not even as
      // `cause`. TanStack serializes a rejected server function with Seroval,
      // which walks `cause` and its own enumerable properties, so an upstream
      // message, a server stack, or a SQLSTATE would be handed to the browser.
      // What crosses the boundary is this bare message and nothing else.
      //
      // `AlreadyReportedError` is what stops the global middleware reporting
      // that replacement on the way out: one failed save is one Sentry event,
      // the one with the cause in it.
      const { AlreadyReportedError, reportToSentry } =
        await import("~/lib/server/sentry-report");
      reportToSentry(error, "server_function");

      throw new AlreadyReportedError("Failed to save user preferences");
    }
  });

export const Route = createFileRoute(
  "/_user/settings/profile/coaching-preferences",
)({
  staticData: { title: "Coaching preferences" },
  head: () =>
    createNoIndexMeta(
      "Coaching Preferences – Dream Coach Match",
      "Manage your coaching preferences and requirements",
    ),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["user-preferences"],
      queryFn: () => getFormData(),
    });
    return {};
  },
  component: CoachingPreferencesPage,
});

function SectionCard({
  icon,
  title,
  children,
  delay = 0,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Card>
        <SettingsCardTitle icon={icon}>{title}</SettingsCardTitle>
        {children}
      </Card>
    </motion.div>
  );
}

function CoachingPreferencesPage() {
  const { data: initialData } = useQuery({
    queryKey: ["user-preferences"],
    queryFn: () => getFormData(),
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState,
    control,
  } = useForm<CoachDiscoveryForm>({
    resolver: zodResolver(CoachDiscoverySchema),
    defaultValues: initialData || {},
  });

  const { errors, isSubmitting } = formState;

  const updateMutation = useMutation({
    mutationFn: (data: CoachDiscoveryForm) =>
      updateFormData({ data: { data } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
      toast.success("Preferences saved");
    },
    onError: (_error: Error) => {
      toast.error("Failed to save preferences. Please try again.");
    },
  });

  const onSubmit = (data: CoachDiscoveryForm) => {
    updateMutation.mutate(data);
  };

  const watchedGoals = watch("goals");
  const watchedFaithIntegration = watch("faithIntegration");
  const watchedPrefersCoachLocation = watch("prefersCoachLocation");

  const getErrorMessage = (error: unknown): string | undefined => {
    if (!error) {
      return undefined;
    }

    if (typeof error === "string") {
      return error;
    }

    if (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      typeof (error as { message?: unknown }).message === "string"
    ) {
      return (error as { message: string }).message;
    }

    return undefined;
  };

  const validateOtherGoal = (value: string | null | undefined) => {
    const goals = getValues("goals") as string[] | undefined;
    if (goals?.includes("Other") && !value) {
      return "Please describe your goal in the text area if you selected 'Other'.";
    }
    return true;
  };

  const validateOtherFaith = (value: string | undefined) => {
    const preference = getValues("faithIntegration") as string[];
    if (preference?.includes("Other") && !value) {
      return "Please describe your faith in the text area if you selected 'Other'.";
    }
    return true;
  };

  const isSaving = isSubmitting || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {/* Save button top */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex justify-end"
      >
        <Button
          type="submit"
          variant="primary"
          size="sm"
          className="gap-1.5"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
      </motion.div>

      {/* Goals & timing */}
      <SectionCard icon={Target} title="Goals & timing" delay={0}>
        <FormField label="Goals" error={getErrorMessage(errors.goals)}>
          <Controller
            name="goals"
            control={control}
            render={({ field }) => (
              <>
                {goalsField.options?.map(({ value, label }) => (
                  <div key={value} className="flex items-center">
                    <Checkbox
                      checked={field.value?.includes(value) || false}
                      onCheckedChange={(checked) => {
                        const currentValue = field.value || [];
                        if (checked) {
                          field.onChange([...currentValue, value]);
                        } else {
                          field.onChange(
                            currentValue.filter((v: string) => v !== value),
                          );
                        }
                      }}
                    >
                      {label}
                    </Checkbox>
                  </div>
                ))}
              </>
            )}
          />
        </FormField>

        {/* Other Goal */}
        <div
          className={cn(
            "max-h-0 overflow-hidden px-1 opacity-0 transition-[max-height,opacity] duration-700",
            {
              "max-h-fit opacity-100": watchedGoals?.includes("Other"),
            },
          )}
        >
          <FormField error={getErrorMessage(errors.otherGoal)}>
            <Textarea
              {...register("otherGoal", { validate: validateOtherGoal })}
              className="mt-0.5 resize-none"
              maxLength={otherGoalField.maxLength}
            />
          </FormField>
        </div>

        <FormField
          labelSize="sm"
          error={getErrorMessage(errors.startTime)}
          label={startTimeField.subTitle || ""}
        >
          <Controller
            name="startTime"
            control={control}
            render={({ field }) => (
              <RadioGroup
                className="mt-3 flex flex-col gap-y-3"
                value={field.value}
                onValueChange={field.onChange}
              >
                {startTimeField.options?.map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`startTime-${value}`} />
                    <label
                      htmlFor={`startTime-${value}`}
                      className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </FormField>

        <FormField
          labelSize="sm"
          error={getErrorMessage(errors.support)}
          label={supportField.subTitle || ""}
        >
          <Controller
            name="support"
            control={control}
            render={({ field }) => (
              <RadioGroup
                className="mt-3 flex flex-col gap-y-3"
                value={field.value}
                onValueChange={field.onChange}
              >
                {supportField.options?.map(({ value, label, description }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`support-${value}`} />
                    <div className="grid gap-1.5 leading-none">
                      <label
                        htmlFor={`support-${value}`}
                        className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {label}
                      </label>
                      {description && (
                        <p className="text-sm text-muted-foreground">
                          {description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </FormField>
      </SectionCard>

      {/* Coaching format & style */}
      <SectionCard icon={Sparkles} title="Coaching format & style" delay={0.06}>
        <FormField
          label={coachingFormatsFields.subTitle || ""}
          error={getErrorMessage(errors.coachingFormats)}
        >
          <Controller
            name="coachingFormats"
            control={control}
            render={({ field }) => (
              <RadioGroup value={field.value} onValueChange={field.onChange}>
                {coachingFormatsFields.options?.map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={value}
                      id={`coachingFormats-${value}`}
                    />
                    <label
                      htmlFor={`coachingFormats-${value}`}
                      className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </FormField>

        <FormField
          label="Coaching Style"
          error={getErrorMessage(errors.coachingApproaches)}
        >
          <CustomMultiSelect
            field={{
              name: "coachingApproaches",
              value: watch("coachingApproaches") || [],
            }}
            props={register("coachingApproaches")}
            placeholder=""
            variant="slim"
            exclusiveValues={["Not sure yet"]}
            items={coachingApproachesField.options || []}
          />
        </FormField>

        <FormField
          label="Coaching experience levels"
          error={getErrorMessage(errors.coachingPackages)}
        >
          <CustomMultiSelect
            field={{
              name: "coachingPackages",
              value: watch("coachingPackages") || [],
            }}
            props={register("coachingPackages")}
            placeholder=""
            variant="slim"
            items={coachingPackagesField.options || []}
            exclusiveValues={["No preference"]}
          />
        </FormField>
      </SectionCard>

      {/* Coach characteristics */}
      <SectionCard icon={Users} title="Coach characteristics" delay={0.12}>
        <FormField label="Coach ages" error={getErrorMessage(errors.coachAges)}>
          <CustomMultiSelect
            field={{
              name: "coachAges",
              value: watch("coachAges") || [],
            }}
            props={register("coachAges")}
            placeholder=""
            variant="slim"
            items={coachAgesField.options || []}
            exclusiveValues={["No preference"]}
          />
        </FormField>

        <FormField
          label="Coach gender"
          error={getErrorMessage(errors.coachGender)}
        >
          <Controller
            name="coachGender"
            control={control}
            render={({ field }) => (
              <RadioGroup value={field.value} onValueChange={field.onChange}>
                {coachGenderField.options?.map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`coachGender-${value}`} />
                    <label
                      htmlFor={`coachGender-${value}`}
                      className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </RadioGroup>
            )}
          />
        </FormField>
      </SectionCard>

      {/* Personality traits */}
      <SectionCard icon={UserCheck} title="Personality traits" delay={0.18}>
        <FormField
          labelSize="sm"
          error={getErrorMessage(errors.userPersonality)}
          label="Your personality traits"
        >
          <Controller
            name="userPersonality"
            control={control}
            render={({ field }) => (
              <div className="mt-2 flex flex-col gap-y-3">
                {userPersonalityField.options?.map(
                  ({ value, label, description }) => (
                    <div key={value} className="flex items-start gap-2">
                      <Checkbox
                        checked={field.value?.includes(value) || false}
                        onCheckedChange={(checked) => {
                          const currentValue = field.value || [];
                          if (checked) {
                            if (value === "Skip this question") {
                              field.onChange([value]);
                            } else {
                              const filtered = currentValue.filter(
                                (v: string) => v !== "Skip this question",
                              );
                              field.onChange([...filtered, value]);
                            }
                          } else {
                            field.onChange(
                              currentValue.filter((v: string) => v !== value),
                            );
                          }
                        }}
                      />
                      <div className="grid w-full gap-1.5 leading-none">
                        <label className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {label}
                        </label>
                        {description && (
                          <p className="text-text-tertiary text-sm">
                            {description}
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          />
        </FormField>

        <FormField
          labelSize="sm"
          error={getErrorMessage(errors.coachPersonality)}
          label="Preferred coach personality traits"
        >
          <Controller
            name="coachPersonality"
            control={control}
            render={({ field }) => (
              <div className="mt-2 flex flex-col gap-y-3">
                {coachPersonalityField.options?.map(
                  ({ value, label, description }) => (
                    <div key={value} className="flex items-start gap-2">
                      <Checkbox
                        checked={field.value?.includes(value) || false}
                        onCheckedChange={(checked) => {
                          const currentValue = field.value || [];
                          if (checked) {
                            if (value === "Skip this question") {
                              field.onChange([value]);
                            } else {
                              const filtered = currentValue.filter(
                                (v: string) => v !== "Skip this question",
                              );
                              field.onChange([...filtered, value]);
                            }
                          } else {
                            field.onChange(
                              currentValue.filter((v: string) => v !== value),
                            );
                          }
                        }}
                      />
                      <div className="grid w-full gap-1.5 leading-none">
                        <label className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {label}
                        </label>
                        {description && (
                          <p className="text-text-tertiary text-sm">
                            {description}
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          />
        </FormField>
      </SectionCard>

      {/* Session delivery & location */}
      <SectionCard
        icon={Globe}
        title="Session delivery & location"
        delay={0.24}
      >
        <FormField
          label={sessionDeliveryMethodsFeild.subTitle || ""}
          error={getErrorMessage(errors.sessionDeliveryMethods)}
        >
          <Controller
            name="sessionDeliveryMethods"
            control={control}
            render={({ field }) => (
              <RadioGroup value={field.value} onValueChange={field.onChange}>
                {sessionDeliveryMethodsFeild.options?.map(
                  ({ value, label }) => (
                    <RadioGroupItem key={value} value={value}>
                      {label}
                    </RadioGroupItem>
                  ),
                )}
              </RadioGroup>
            )}
          />
        </FormField>

        <FormField
          label={prefersCoachLocationField.subTitle || ""}
          error={getErrorMessage(errors.prefersCoachLocation)}
        >
          <Controller
            name="prefersCoachLocation"
            control={control}
            render={({ field }) => (
              <RadioGroup value={field.value} onValueChange={field.onChange}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    key={prefersCoachLocationField?.options?.[0]?.value}
                    value={prefersCoachLocationField?.options?.[0]?.value}
                    id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                  />
                  <label
                    htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                    className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {prefersCoachLocationField?.options?.[0]?.label || ""}
                  </label>
                </div>

                {/* Conditional Location Details */}
                <div
                  className={cn(
                    "max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-700",
                    {
                      "max-h-screen opacity-100":
                        watchedPrefersCoachLocation?.includes("Yes"),
                    },
                  )}
                >
                  <div className="mt-4 flex flex-col gap-4 px-1">
                    <FormField
                      label={coachLocationCountryField.subTitle}
                      error={getErrorMessage(errors.coachLocationCountry)}
                    >
                      <CountrySelect
                        value={watch("coachLocationCountry")}
                        onChange={(value) => {
                          setValue("coachLocationCountry", value, {
                            shouldValidate: true,
                          });
                        }}
                      />
                    </FormField>

                    <FormField
                      label={coachLocationRegionField.subTitle || ""}
                      error={getErrorMessage(errors.coachLocationRegion)}
                    >
                      <Input {...register("coachLocationRegion")} />
                    </FormField>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    key={prefersCoachLocationField?.options?.[1]?.value}
                    value={prefersCoachLocationField?.options?.[1]?.value}
                    id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                  />
                  <label
                    htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                    className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {prefersCoachLocationField?.options?.[1]?.label || ""}
                  </label>
                </div>
              </RadioGroup>
            )}
          />
        </FormField>
      </SectionCard>

      {/* Faith & language */}
      <SectionCard icon={Heart} title="Faith & language" delay={0.3}>
        <FormField
          label="Faith preferences"
          error={getErrorMessage(errors.faithIntegration)}
        >
          <Controller
            name="faithIntegration"
            control={control}
            render={({ field }) => (
              <>
                {faithIntegrationsField.options?.map(({ value, label }) => (
                  <div key={value} className="flex items-center">
                    <Checkbox
                      checked={field.value?.includes(value) || false}
                      onCheckedChange={(checked) => {
                        const currentValue = field.value || [];

                        if (checked) {
                          if (value === "No preference") {
                            field.onChange(["No preference"]);
                            setValue("otherFaithIntegration", undefined);
                          } else {
                            field.onChange([
                              ...currentValue.filter(
                                (v: string) => v !== "No preference",
                              ),
                              value,
                            ]);
                          }
                        } else {
                          field.onChange(
                            currentValue.filter((v: string) => v !== value),
                          );
                        }
                      }}
                    >
                      {label}
                    </Checkbox>
                  </div>
                ))}
              </>
            )}
          />
        </FormField>

        {/* Other Faith Textarea */}
        <div
          className={cn(
            "max-h-0 overflow-hidden px-1 opacity-0 transition-[max-height,opacity] duration-700",
            {
              "max-h-fit opacity-100":
                watchedFaithIntegration?.includes("Other"),
            },
          )}
        >
          <FormField error={getErrorMessage(errors.otherFaithIntegration)}>
            <Textarea
              {...register("otherFaithIntegration", {
                validate: validateOtherFaith,
              })}
              className="resize-none"
              maxLength={otherFaithIntegrationField.maxLength}
              placeholder={otherFaithIntegrationField.placeholder}
            />
          </FormField>
        </div>

        <FormField
          label="Language"
          error={getErrorMessage(errors.preferredLanguages)}
        >
          <LanguageSelect
            field={{
              name: "preferredLanguages",
              value: watch("preferredLanguages"),
            }}
            props={register("preferredLanguages")}
            variant="slim"
            placeholder=""
          />
        </FormField>
      </SectionCard>

      {/* Personal context */}
      <SectionCard icon={MessageSquare} title="Personal context" delay={0.36}>
        <FormField
          label="Share anything else that would help us match you"
          error={getErrorMessage(errors.personalContext)}
        >
          <Controller
            name="personalContext"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                className="resize-none"
                maxLength={personalContextField.maxLength}
                placeholder={personalContextField.placeholder}
              />
            )}
          />
        </FormField>
      </SectionCard>

      {/* Save button bottom */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="flex justify-end"
      >
        <Button
          type="submit"
          variant="primary"
          className="gap-1.5"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
      </motion.div>
    </form>
  );
}
